/**
 * Stored login credentials (AsyncStorage) + minimal Keychain biometric gate.
 *
 * User passwords are **only** in AsyncStorage (`STORED_LOGIN_CREDENTIALS_KEY`). They persist across
 * logout. Keychain holds a dummy generic password so `getGenericPassword` triggers Face ID / fingerprint.
 *
 * RN does not expose biometrics in JS without a native module; this project already depends on
 * `react-native-keychain`. No extra packages are required.
 *
 * Used by: `LoginSheetContent` v1/v3; `OTPVerificationSheetContent` v1/v2/v3; `shared/auth/index`;
 * `clearCredentials` for explicit “forget device login” only — **not** `logout` / `clearSessionFromStorage`.
 */
import * as Keychain from 'react-native-keychain';
import AsyncStorage from '@react-native-async-storage/async-storage';

import { BIOMETRIC_OFFER_DECIDED_KEY, STORED_LOGIN_CREDENTIALS_KEY } from './session/keys';

const KEYCHAIN_BIOMETRIC_GATE_SERVICE = 'EataamiBiometricGate';

export interface StoredCredentials {
  username: string;
  password: string;
}

function serialize_credentials(username: string, password: string): string {
  return JSON.stringify({ username, password });
}

export async function markBiometricOfferDecided(): Promise<void> {
  await AsyncStorage.setItem(BIOMETRIC_OFFER_DECIDED_KEY, '1');
}

/** True when the enable-biometrics alert was already accepted or declined on this device. */
export async function hasBiometricOfferBeenDecided(): Promise<boolean> {
  try {
    const raw = await AsyncStorage.getItem(BIOMETRIC_OFFER_DECIDED_KEY);
    return raw === '1';
  } catch {
    return false;
  }
}

export async function setCredentials(username: string, password: string): Promise<void> {
  await AsyncStorage.setItem(STORED_LOGIN_CREDENTIALS_KEY, serialize_credentials(username, password));
  await markBiometricOfferDecided();
  try {
    await Keychain.setGenericPassword('gate', 'gate', {
      service: KEYCHAIN_BIOMETRIC_GATE_SERVICE,
      accessControl: Keychain.ACCESS_CONTROL.BIOMETRY_ANY,
      accessible: Keychain.ACCESSIBLE.WHEN_UNLOCKED,
    });
  } catch {
    // Credentials still saved; biometric gate may be unavailable until device supports it.
  }
}

export async function loadStoredCredentials(): Promise<StoredCredentials | null> {
  try {
    const raw = await AsyncStorage.getItem(STORED_LOGIN_CREDENTIALS_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as { username?: string; password?: string };
    if (typeof parsed.username === 'string' && typeof parsed.password === 'string') {
      return { username: parsed.username, password: parsed.password };
    }
    return null;
  } catch {
    return null;
  }
}

export type BiometricPromptStrings = {
  title?: string;
  subtitle?: string;
  cancel?: string;
};

/** OS biometric sheet via Keychain gate only (dummy secret). */
export async function authenticateWithBiometrics(prompt?: BiometricPromptStrings): Promise<boolean> {
  try {
    const result = await Keychain.getGenericPassword({
      service: KEYCHAIN_BIOMETRIC_GATE_SERVICE,
      authenticationPrompt: {
        title: prompt?.title ?? 'Authenticate',
        subtitle: prompt?.subtitle ?? 'Verify your identity to log in',
        cancel: prompt?.cancel,
      },
    });
    return !!(result && result.password);
  } catch {
    return false;
  }
}

export async function hasStoredCredentials(): Promise<boolean> {
  const creds = await loadStoredCredentials();
  return creds != null;
}

/** True when device reports enrolled Face ID / fingerprint (etc.). */
export async function isDeviceBiometryReady(): Promise<boolean> {
  try {
    const biometry_type = await Keychain.getSupportedBiometryType();
    return biometry_type != null;
  } catch {
    return false;
  }
}

/** Clears AsyncStorage credentials and Keychain gate. Call only for explicit “forget device login”. */
export async function clearCredentials(): Promise<void> {
  try {
    await Keychain.resetGenericPassword({ service: KEYCHAIN_BIOMETRIC_GATE_SERVICE });
  } catch {
    // ignore
  }
  await AsyncStorage.multiRemove([STORED_LOGIN_CREDENTIALS_KEY, BIOMETRIC_OFFER_DECIDED_KEY]);
}
